import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { descargarArchivo } from "../../utils/download";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Switch } from "../../components/ui/switch";
import {
  Plus, Search, Edit, Trash2, ImageIcon, Share2, MessageCircle,
  ExternalLink, GripVertical, DollarSign, Tag, Eye, Upload, Link as LinkIcon, Download, X
} from "lucide-react";
import CatalogoSubnav from "../../components/CatalogoSubnav";
import { ConfiguracionOperativaFields, configuracionOperativaEmpty } from "../../components/ConfiguracionOperativaFields";

const categorias = [
  "Invitaciones", "Decoración", "Montaje", "Catering", "Fotografía",
  "Música/DJ", "Iluminación", "Flores", "Mobiliario", "Paquetes", "Otro"
];

const Catalogo = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    nombre: "", descripcion: "", precio: "", categoria: "Invitaciones",
    imagen_url: "", activo: true, visible_publico: true, orden: 0,
    tipo: "general", precio_nino: "", costo_interno: 0,
    es_menu_principal: false, es_paquete_completo: false, incluye_instalaciones: false, sub_servicios: [],
    // FASE 2 · Campos adicionales
    codigo: "", subcategoria: "", unidad_medida: "pieza", cantidad_disponible: 0,
    proveedor_id: "", unidad_negocio: "", compartido: false, notas_internas: "",
    receta_id: "",  // Iter112 · vincula item de menú con receta para materialización
    configuracion_operativa: { ...configuracionOperativaEmpty },
  });
  const [proveedoresLista, setProveedoresLista] = useState([]);
  const [recetasLista, setRecetasLista] = useState([]);  // Iter112
  const [showAvanzado, setShowAvanzado] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialItems, setHistorialItems] = useState([]);
  const [seedLoading, setSeedLoading] = useState(false);
  const [unidadesTenant, setUnidadesTenant] = useState([]);  // Iter101 · unidades del propio tenant (no hardcode Celebra)

  const catalogUrl = `${window.location.origin}/catalogo`;
  const [verArchivados, setVerArchivados] = useState(false);
  const [cotSelectDialog, setCotSelectDialog] = useState({ open: false, item: null });
  const [cotizacionesLista, setCotizacionesLista] = useState([]);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/catalogo${verArchivados ? "/archivados" : ""}`);
      setItems(res.data);
      // También cargar proveedores (para el select del formulario)
      const p = await axios.get(`${API}/proveedores`).catch(() => ({ data: [] }));
      setProveedoresLista(p.data || []);
      // Iter112 · cargar recetas para vincular menú↔receta
      const rec = await axios.get(`${API}/recetas`).catch(() => ({ data: [] }));
      setRecetasLista(rec.data || []);
      // Iter101 · cargar unidades del tenant (no hardcode Celebra)
      const u = await axios.get(`${API}/business-units`).catch(() => ({ data: { unidades: [] } }));
      setUnidadesTenant(u.data?.unidades || []);
    } catch {
      toast.error("Error al cargar catálogo");
    } finally {
      setLoading(false);
    }
  }, [verArchivados]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const cargarPlantillas = async () => {
    setSeedLoading(true);
    try {
      const { data } = await axios.post(`${API}/tenant/seed-catalogo-demo`);
      const n = (data?.agregados?.catalogo || 0) + (data?.agregados?.experiencias || 0);
      if (n > 0) toast.success(`${n} items agregados. Puedes editarlos cuando quieras.`);
      else toast.info("Ya tenías las plantillas de ejemplo cargadas.");
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar plantillas");
    } finally { setSeedLoading(false); }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("El archivo es muy grande (máximo 10MB)");
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // NO seteamos Content-Type — axios detecta FormData y agrega boundary automático
      const res = await axios.post(`${API}/upload`, fd);
      const imgUrl = res.data.url || "";  // Iter109 · URL RELATIVA (/api/files/...) para que sobreviva a deploys entre preview↔producción. Antes se hardcodeaba BACKEND_URL y en producción las imágenes fallaban por cross-domain.
      setFormData(prev => ({ ...prev, imagen_url: imgUrl }));
      toast.success("Imagen subida correctamente");
      // Limpiar el input para permitir subir la misma imagen otra vez
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir imagen. Verifica el archivo.");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.nombre) {
      toast.error("El nombre es requerido");
      return;
    }
    try {
      const payload = {
        ...formData,
        precio: parseFloat(formData.precio) || 0,
        precio_nino: formData.precio_nino !== "" && formData.precio_nino !== null ? parseFloat(formData.precio_nino) : null,
        costo_interno: parseFloat(formData.costo_interno) || 0,
        orden: parseInt(formData.orden) || 0,
        cantidad_disponible: parseFloat(formData.cantidad_disponible) || 0,
        proveedor_id: formData.proveedor_id || null,
      };
      // Compartido escribe unidad="compartido" (compat mecanismo legado)
      if (payload.compartido) {
        payload.unidad = "compartido";
        payload.unidad_negocio = null;
      } else if (payload.unidad_negocio) {
        payload.unidad = payload.unidad_negocio;
      }
      // Si es paquete completo, fijar incluye_instalaciones=true por consistencia
      if (payload.es_paquete_completo) {
        payload.incluye_instalaciones = true;
        payload.tipo = payload.tipo === "general" ? "paquete" : payload.tipo;
      }
      if (editingItem) {
        await axios.put(`${API}/catalogo/${editingItem.item_id}`, payload);
        toast.success("Item actualizado correctamente");
      } else {
        await axios.post(`${API}/catalogo`, payload);
        toast.success("Item creado");
      }
      resetForm();
      await fetchItems();
      setDialogOpen(false);
    } catch (error) {
      toast.error("Error al guardar. Reintenta.");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/catalogo/${deleteId}`);
      toast.success("Item desactivado (soft delete)");
      await fetchItems();
    } catch (error) {
      toast.error("Error al desactivar");
    } finally {
      setDeleteId(null);
    }
  };

  const handleToggleActivo = async (item) => {
    try {
      await axios.put(`${API}/catalogo/${item.item_id}`, { activo: !item.activo });
      await fetchItems();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const cargarCotizaciones = async () => {
    try {
      const r = await axios.get(`${API}/cotizaciones`);
      const abiertas = (r.data || []).filter(c => !["cancelada", "finalizada", "convertida"].includes(c.estatus));
      setCotizacionesLista(abiertas);
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[Catalogo] cotizaciones fetch:", err?.message); toast.error("No se pudo cargar cotizaciones"); }
  };

  const duplicarItem = async (item) => {
    try {
      await axios.post(`${API}/catalogo/${item.item_id}/duplicar`);
      toast.success("Item duplicado. Edítalo para personalizarlo.");
      await fetchItems();
    } catch { toast.error("No se pudo duplicar"); }
  };

  const restaurarItem = async (item) => {
    try {
      await axios.post(`${API}/catalogo/${item.item_id}/restaurar`);
      toast.success("Item restaurado");
      await fetchItems();
    } catch { toast.error("No se pudo restaurar"); }
  };

  const eliminarDefinitivo = async (item) => {
    if (!window.confirm(`⚠️ ELIMINAR DEFINITIVAMENTE "${item.nombre}"?\n\nEsto NO se puede deshacer. El item desaparecerá para siempre.`)) return;
    try {
      await axios.post(`${API}/catalogo/${item.item_id}/eliminar-definitivo`);
      toast.success("Eliminado definitivamente");
      await fetchItems();
    } catch (err) { toast.error("No se pudo eliminar: " + (err?.response?.data?.detail || err.message)); }
  };

  const abrirAgregarACotizacion = async (item) => {
    await cargarCotizaciones();
    setCotSelectDialog({ open: true, item });
  };

  const agregarACotizacion = async (cotizacion_id) => {
    const item = cotSelectDialog.item;
    if (!item) return;
    try {
      await axios.post(`${API}/catalogo/${item.item_id}/agregar-cotizacion`, { cotizacion_id, cantidad: 1 });
      toast.success(`"${item.nombre}" agregado a la cotización`);
      setCotSelectDialog({ open: false, item: null });
    } catch (err) { toast.error("Error: " + (err?.response?.data?.detail || err.message)); }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      nombre: item.nombre, descripcion: item.descripcion || "",
      precio: item.precio?.toString() || "0", categoria: item.categoria || "Invitaciones",
      imagen_url: item.imagen_url || "", activo: item.activo, visible_publico: item.visible_publico || false, orden: item.orden || 0,
      tipo: item.tipo || "general",
      precio_nino: item.precio_nino?.toString() || "",
      costo_interno: item.costo_interno || 0,
      es_menu_principal: !!item.es_menu_principal,
      es_paquete_completo: !!item.es_paquete_completo,
      sub_servicios: item.sub_servicios || [],
      incluye_instalaciones: !!item.incluye_instalaciones,
      // FASE 2
      codigo: item.codigo || "",
      subcategoria: item.subcategoria || "",
      unidad_medida: item.unidad_medida || "pieza",
      cantidad_disponible: item.cantidad_disponible ?? 0,
      proveedor_id: item.proveedor_id || "",
      unidad_negocio: item.unidad_negocio || item.unidad || "",
      compartido: (item.unidad === "compartido") || !!item.compartido,
      notas_internas: item.notas_internas || "",
      receta_id: item.receta_id || "",  // Iter112
      configuracion_operativa: { ...configuracionOperativaEmpty, ...(item.configuracion_operativa || {}) },
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      nombre: "", descripcion: "", precio: "", categoria: "Invitaciones",
      imagen_url: "", activo: true, visible_publico: true, orden: 0,
      tipo: "general", precio_nino: "", costo_interno: 0,
      es_menu_principal: false, es_paquete_completo: false, incluye_instalaciones: false, sub_servicios: [],
      codigo: "", subcategoria: "", unidad_medida: "pieza", cantidad_disponible: 0,
      proveedor_id: "", unidad_negocio: "", compartido: false, notas_internas: "",
      receta_id: "",  // Iter112
      configuracion_operativa: { ...configuracionOperativaEmpty },
    });
  };

  const shareWhatsApp = (item) => {
    const msg = `*${item.nombre}*%0A${item.descripcion || ''}%0APrecio: $${item.precio?.toLocaleString()} MXN%0A%0AVe nuestro catálogo completo: ${catalogUrl}`;
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const shareFullCatalog = () => {
    const msg = `*CelebraMid - Catálogo de Servicios*%0AVe nuestro catálogo completo aquí:%0A${catalogUrl}`;
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(catalogUrl);
    toast.success("Enlace copiado al portapapeles");
  };

  const filtered = items.filter(item => {
    const matchSearch = item.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.descripcion || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = filterCategoria === "todos" || item.categoria === filterCategoria;
    return matchSearch && matchCat;
  });

  const totalItems = items.filter(i => verArchivados ? !i.activo : i.activo).length;

  return (
    <div className="space-y-6">
      <CatalogoSubnav />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-stone-900">Catálogo{verArchivados && <span className="text-base text-stone-500 ml-2">· Archivados</span>}</h1>
          <p className="text-stone-600 mt-1">{totalItems} {verArchivados ? "items archivados" : "productos/servicios activos"}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={verArchivados ? "default" : "outline"}
            onClick={() => setVerArchivados(v => !v)}
            className={verArchivados ? "bg-stone-800 text-white hover:bg-stone-900" : "border-stone-300"}
            data-testid="btn-toggle-archivados"
          >
            {verArchivados ? "Ver activos" : "Archivados"}
          </Button>
          <a href={`${BACKEND_URL}/api/admin/exportar/pdf/catalogo/download`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-stone-300 bg-white hover:bg-stone-50 h-9 px-4" data-testid="btn-descargar-catalogo">
            <Download className="w-4 h-4 mr-2" />
            Descargar PDF
          </a>
          <Button variant="outline" onClick={() => setShareDialogOpen(true)} className="border-stone-300" data-testid="btn-compartir-catalogo">
            <Share2 className="w-4 h-4 mr-2" />
            Compartir
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm(`¿Vaciar TODO el catálogo? Se archivarán ${totalItems} items (podrás recuperarlos desde "Archivados"). Esta acción NO borra permanentemente.`)) return;
              try {
                const { data } = await axios.post(`${API}/catalogo/vaciar-todo`, {});
                toast.success(`Catálogo vaciado. ${data.desactivados || data.eliminados || 0} items archivados.`);
                fetchItems();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Error al vaciar el catálogo");
              }
            }}
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="btn-vaciar-catalogo"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Vaciar todo
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="btn-nuevo-item">
            <Plus className="w-4 h-4 mr-2" />
            + Crear producto o servicio
          </Button>
          {items.length === 0 && (
            <Button
              variant="outline"
              onClick={cargarPlantillas}
              disabled={seedLoading}
              data-testid="btn-cargar-plantillas-catalogo"
            >
              {seedLoading ? "Cargando…" : "Cargar plantillas de ejemplo"}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input placeholder="Buscar en catálogo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 border-stone-300" data-testid="search-catalogo" />
        </div>
        <Select value={filterCategoria} onValueChange={setFilterCategoria}>
          <SelectTrigger className="w-full md:w-48 border-stone-300" data-testid="filter-categoria">
            <Tag className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas las categorías</SelectItem>
            {categorias.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(item => (
            <Card key={item.item_id} className={`border-stone-200 overflow-hidden hover:shadow-lg transition-shadow ${!item.activo ? 'opacity-50' : ''}`} data-testid={`catalogo-card-${item.item_id}`}>
              <div className="aspect-square bg-stone-100 relative overflow-hidden">
                {item.imagen_url ? (
                  <img src={item.imagen_url} alt={item.nombre} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-stone-300" />
                  </div>
                )}
                {!item.activo && (
                  <div className="absolute top-2 left-2">
                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">Inactivo</span>
                  </div>
                )}
                {item.visible_publico && item.activo && (
                  <div className="absolute bottom-2 left-2">
                    <span className="bg-amber-500 text-white text-xs px-2 py-1 rounded-full">Público</span>
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <span className="bg-white/90 backdrop-blur text-xs px-2 py-1 rounded-full text-stone-600 font-medium">{item.categoria}</span>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-medium text-stone-900 text-sm mb-1 line-clamp-1">{item.nombre}</h3>
                {item.descripcion && (
                  <p className="text-xs text-stone-500 mb-3 line-clamp-2">{item.descripcion}</p>
                )}
                <div className="flex items-center justify-between mb-3">
                  <span className="font-display text-lg text-emerald-900">${item.precio?.toLocaleString()} <span className="text-xs text-stone-400">MXN</span></span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {verArchivados ? (
                    <>
                      <Button variant="outline" size="sm" onClick={() => restaurarItem(item)} className="border-emerald-300 text-emerald-700 flex-1 text-xs" data-testid={`btn-restore-${item.item_id}`}>
                        <Upload className="w-3 h-3 mr-1" />Restaurar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => eliminarDefinitivo(item)} className="border-red-400 text-red-700 hover:bg-red-50" data-testid={`btn-hard-del-${item.item_id}`} title="Eliminar definitivamente">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(item)} className="border-stone-300 flex-1 text-xs" data-testid={`btn-edit-${item.item_id}`}>
                        <Edit className="w-3 h-3 mr-1" />Editar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => duplicarItem(item)} className="border-stone-300 text-xs" data-testid={`btn-dup-${item.item_id}`} title="Duplicar">
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => abrirAgregarACotizacion(item)} className="border-amber-300 text-amber-800 hover:bg-amber-50 text-xs" data-testid={`btn-add-cot-${item.item_id}`} title="Agregar a cotización">
                        <DollarSign className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => shareWhatsApp(item)} className="border-green-300 text-green-700 hover:bg-green-50" data-testid={`btn-share-${item.item_id}`} title="Compartir WhatsApp">
                        <MessageCircle className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDeleteId(item.item_id)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`btn-arch-${item.item_id}`} title="Archivar">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ImageIcon className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="font-display text-xl text-stone-900 mb-2">Catálogo vacío</h3>
          <p className="text-stone-600 mb-4">Agrega productos y servicios para compartir con tus clientes</p>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Item
          </Button>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-1rem)] max-h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="catalogo-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-200 flex-shrink-0">
            <DialogTitle className="font-display text-xl">
              {editingItem ? "Editar Item" : "Nuevo Item de Catálogo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 px-5 flex-1 overflow-y-auto" data-testid="catalogo-dialog-body">
            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Imagen</Label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 bg-stone-100 rounded-lg overflow-hidden flex-shrink-0 border border-stone-200">
                  {formData.imagen_url ? (
                    <img src={formData.imagen_url} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-stone-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input type="file" ref={fileInputRef} onChange={handleUploadImage} accept="image/*,.pdf" className="hidden" />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="border-stone-300 w-full" data-testid="btn-upload-imagen">
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? "Subiendo..." : "Subir imagen"}
                  </Button>
                  <Input value={formData.imagen_url} onChange={(e) => setFormData(prev => ({ ...prev, imagen_url: e.target.value }))} placeholder="O pega una URL" className="border-stone-300 text-xs" data-testid="input-imagen-url" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={formData.nombre} onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))} className="border-stone-300" data-testid="input-nombre-catalogo" />
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={formData.descripcion} onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))} rows={3} className="border-stone-300" data-testid="input-desc-catalogo" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Precio (MXN)</Label>
                <Input type="number" value={formData.precio} onChange={(e) => setFormData(prev => ({ ...prev, precio: e.target.value }))} className="border-stone-300" data-testid="input-precio-catalogo" />
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={formData.categoria} onValueChange={(v) => setFormData(prev => ({ ...prev, categoria: v }))}>
                  <SelectTrigger className="border-stone-300" data-testid="select-categoria-catalogo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Orden</Label>
                <Input type="number" value={formData.orden} onChange={(e) => setFormData(prev => ({ ...prev, orden: parseInt(e.target.value) || 0 }))} className="border-stone-300" />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={formData.activo} onCheckedChange={(v) => setFormData(prev => ({ ...prev, activo: v }))} />
                <Label className="text-sm">Activo (visible en catálogo)</Label>
              </div>
            </div>
            <div className="border-t border-stone-200 pt-3 space-y-3">
              <p className="text-sm font-semibold text-stone-700">Para el cotizador automatico (menus y paquetes)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <Select value={formData.tipo} onValueChange={(v) => setFormData(p => ({ ...p, tipo: v }))}>
                    <SelectTrigger className="h-10" data-testid="cat-select-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General (no entra al cotizador)</SelectItem>
                      <SelectItem value="menu">Menu (por persona)</SelectItem>
                      <SelectItem value="paquete">Paquete completo</SelectItem>
                      <SelectItem value="instalaciones">Instalaciones</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Precio nino (opcional)</Label>
                  <Input type="number" step="0.01" value={formData.precio_nino} onChange={(e) => setFormData(p => ({ ...p, precio_nino: e.target.value }))} placeholder="Si difiere del adulto" data-testid="cat-precio-nino" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Costo interno (privado, no visible al cliente)</Label>
                  <Input type="number" step="0.01" value={formData.costo_interno} onChange={(e) => setFormData(p => ({ ...p, costo_interno: e.target.value }))} data-testid="cat-costo-interno" />
                </div>
              </div>
              <div className="flex items-center justify-between p-2 hover:bg-stone-50 rounded">
                <Label className="flex-1 cursor-pointer text-sm">Menu principal (para minimo operativo)</Label>
                <Switch checked={formData.es_menu_principal} onCheckedChange={(c) => setFormData(p => ({ ...p, es_menu_principal: c }))} data-testid="cat-switch-principal" />
              </div>
              <div className="flex items-center justify-between p-2 bg-emerald-50 border border-emerald-200 rounded">
                <div className="flex-1">
                  <Label className="text-sm cursor-pointer font-medium text-emerald-900">Es paquete completo (instalaciones incluidas)</Label>
                  <p className="text-[10px] text-emerald-700">Al activar, NO se sumara la renta de instalaciones aparte. Se mostrara &quot;Uso de instalaciones: Incluido&quot;.</p>
                </div>
                <Switch checked={formData.es_paquete_completo} onCheckedChange={(c) => setFormData(p => ({ ...p, es_paquete_completo: c, incluye_instalaciones: c ? true : p.incluye_instalaciones }))} data-testid="cat-switch-paquete" />
              </div>
              {formData.es_paquete_completo && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded" data-testid="sub-servicios-editor">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Label className="text-sm font-medium text-purple-900">Sub-servicios del paquete</Label>
                      <p className="text-[10px] text-purple-700">Al seleccionar este paquete en un evento, se expandirán estos sub-servicios en el contrato.</p>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={() => setFormData(p => ({ ...p, sub_servicios: [...(p.sub_servicios || []), { categoria: "", nombre: "" }] }))} data-testid="btn-add-sub-servicio">+ Agregar</Button>
                  </div>
                  {(formData.sub_servicios || []).length === 0 && <p className="text-xs text-purple-600 italic">Sin sub-servicios. Agrega los items que incluye este paquete.</p>}
                  <div className="space-y-1">
                    {(formData.sub_servicios || []).map((sub, i) => (
                      <div key={i} className="flex gap-1" data-testid={`row-sub-${i}`}>
                        <Input value={sub.categoria || ""} onChange={(e) => setFormData(p => { const arr = [...p.sub_servicios]; arr[i] = { ...arr[i], categoria: e.target.value }; return { ...p, sub_servicios: arr }; })} placeholder="Categoría" className="w-32 text-sm" />
                        <Input value={sub.nombre || ""} onChange={(e) => setFormData(p => { const arr = [...p.sub_servicios]; arr[i] = { ...arr[i], nombre: e.target.value }; return { ...p, sub_servicios: arr }; })} placeholder="Nombre del servicio" className="flex-1 text-sm" />
                        <button type="button" onClick={() => setFormData(p => ({ ...p, sub_servicios: p.sub_servicios.filter((_, x) => x !== i) }))} className="text-red-500 p-1"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ══ FASE 2 · Campos avanzados ══ */}
            <div className="border-t border-stone-200 pt-3">
              <button type="button" onClick={() => setShowAvanzado(!showAvanzado)} className="text-sm text-stone-700 hover:text-stone-900 font-medium flex items-center gap-1" data-testid="btn-toggle-avanzado">
                <span>{showAvanzado ? "▼" : "▶"}</span> Campos avanzados (código, subcategoría, medida, cantidad, proveedor, unidad, historial)
              </button>
              {showAvanzado && (
                <div className="mt-3 space-y-3 p-3 bg-stone-50 rounded-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Código / SKU</Label>
                      <Input value={formData.codigo} onChange={(e) => setFormData(p => ({ ...p, codigo: e.target.value }))} placeholder="ej. INV-001" data-testid="cat-codigo" />
                    </div>
                    <div>
                      <Label className="text-xs">Subcategoría</Label>
                      <Input value={formData.subcategoria} onChange={(e) => setFormData(p => ({ ...p, subcategoria: e.target.value }))} placeholder="ej. Digital" data-testid="cat-subcategoria" />
                    </div>
                    <div>
                      <Label className="text-xs">Unidad de medida</Label>
                      <Select value={formData.unidad_medida} onValueChange={(v) => setFormData(p => ({ ...p, unidad_medida: v }))}>
                        <SelectTrigger data-testid="cat-unidad-medida"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["pieza", "unidad", "kg", "g", "l", "ml", "caja", "paquete", "hora", "servicio"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Cantidad disponible</Label>
                      <Input type="number" step="0.01" value={formData.cantidad_disponible} onChange={(e) => setFormData(p => ({ ...p, cantidad_disponible: e.target.value }))} data-testid="cat-cantidad" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Proveedor</Label>
                    <Select value={formData.proveedor_id || "__none__"} onValueChange={(v) => setFormData(p => ({ ...p, proveedor_id: v === "__none__" ? "" : v }))}>
                      <SelectTrigger data-testid="cat-proveedor"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sin asignar</SelectItem>
                        {proveedoresLista.map((pv) => <SelectItem key={pv.proveedor_id} value={pv.proveedor_id}>{pv.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Receta vinculada <span className="text-stone-400">(para materializar producción y compras)</span></Label>
                    <Select value={formData.receta_id || "__none__"} onValueChange={(v) => setFormData(p => ({ ...p, receta_id: v === "__none__" ? "" : v }))}>
                      <SelectTrigger data-testid="cat-receta"><SelectValue placeholder="Sin receta vinculada" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sin receta vinculada</SelectItem>
                        {recetasLista.map((r) => <SelectItem key={r.receta_id} value={r.receta_id}>{r.nombre} · {(r.ingredientes || []).length} ingr.</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Unidad de negocio</Label>
                      <Select value={formData.unidad_negocio || "__none__"} onValueChange={(v) => setFormData(p => ({ ...p, unidad_negocio: v === "__none__" ? "" : v, compartido: false }))} disabled={formData.compartido}>
                        <SelectTrigger data-testid="cat-unidad-negocio"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Sin asignar</SelectItem>
                          {/* Iter101 · Aislamiento: unidades del tenant, no hardcode Celebra */}
                          {(unidadesTenant || []).map(u => (
                            <SelectItem key={u.slug} value={u.slug}>{u.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Switch checked={formData.compartido} onCheckedChange={(c) => setFormData(p => ({ ...p, compartido: c, unidad_negocio: c ? "" : p.unidad_negocio }))} data-testid="cat-compartido" />
                      <Label className="text-sm">Artículo compartido (todas las unidades)</Label>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Notas internas</Label>
                    <Textarea rows={2} value={formData.notas_internas} onChange={(e) => setFormData(p => ({ ...p, notas_internas: e.target.value }))} />
                  </div>
                  {editingItem && (
                    <Button type="button" size="sm" variant="outline" onClick={async () => {
                      try {
                        const r = await axios.get(`${API}/catalogo/${editingItem.item_id}/historial`).catch(() => ({ data: { historial: [] } }));
                        setHistorialItems(r.data.historial || []);
                        setHistorialOpen(true);
                      } catch { toast.error("Error al cargar historial"); }
                    }}>
                      Ver historial de cambios
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-sm">
              <Switch checked={formData.visible_publico} onCheckedChange={(v) => setFormData(prev => ({ ...prev, visible_publico: v }))} data-testid="switch-visible-publico" />
              <div>
                <Label className="text-sm font-medium">Visible en cotizador publico y landing</Label>
                <p className="text-xs text-stone-500">Se mostrara como opcion para el cliente.</p>
              </div>
            </div>

            <ConfiguracionOperativaFields
              value={formData.configuracion_operativa}
              onChange={(cfg) => setFormData(prev => ({ ...prev, configuracion_operativa: cfg }))}
              testidPrefix="config-op-cat"
            />
          </div>
          <DialogFooter className="px-5 py-3 border-t border-stone-200 bg-white flex-shrink-0 gap-2 sm:gap-0 flex-col-reverse sm:flex-row">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-stone-300 w-full sm:w-auto" data-testid="btn-cancelar-catalogo">Cancelar</Button>
            <Button onClick={handleSave} className="btn-primary w-full sm:w-auto" data-testid="btn-guardar-catalogo">
              {editingItem ? "Actualizar" : "Crear"} Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Dialog Historial (FASE 2) */}
      <Dialog open={historialOpen} onOpenChange={setHistorialOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="dialog-historial-catalogo">
          <DialogHeader>
            <DialogTitle>Historial de cambios</DialogTitle>
          </DialogHeader>
          {historialItems.length === 0 ? (
            <p className="text-stone-500 text-sm py-3">Sin entradas de auditoría para este item.</p>
          ) : (
            <div className="space-y-2">
              {historialItems.map((h, i) => (
                <Card key={i} className="border-stone-200">
                  <CardContent className="p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <p className="font-medium capitalize">{h.action?.replace(/_/g, ' ')}</p>
                      <span className="text-xs text-stone-500">{h.timestamp?.slice(0, 19).replace('T', ' ')}</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-1">por {h.usuario_email || h.usuario_id}</p>
                    {h.motivo && <p className="text-xs italic mt-1">{h.motivo}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Compartir Catálogo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Enlace público del catálogo</Label>
              <div className="flex gap-2">
                <Input value={catalogUrl} readOnly className="border-stone-300 bg-stone-50 text-sm" data-testid="catalog-public-url" />
                <Button variant="outline" onClick={copyLink} className="border-stone-300 flex-shrink-0">
                  <LinkIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={shareFullCatalog} className="bg-green-600 hover:bg-green-700 text-white" data-testid="btn-share-whatsapp-catalogo">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
              <a href={catalogUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-stone-300 w-full" data-testid="btn-ver-catalogo">
                  <Eye className="w-4 h-4 mr-2" />
                  Ver catálogo
                </Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar item?</AlertDialogTitle>
            <AlertDialogDescription>El item será marcado como inactivo. No se eliminará de la base de datos y se conservará en históricos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Desactivar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Agregar a cotización */}
      <Dialog open={cotSelectDialog.open} onOpenChange={(v) => setCotSelectDialog(s => ({ ...s, open: v }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Agregar a cotización</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600 mb-3">
            Elige una cotización abierta para agregar <strong>{cotSelectDialog.item?.nombre}</strong>.
          </p>
          {cotizacionesLista.length === 0 ? (
            <p className="text-sm text-stone-500 text-center py-6">
              No hay cotizaciones abiertas. Crea una primero desde Pipeline.
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-1" data-testid="cot-list">
              {cotizacionesLista.map(c => (
                <button
                  key={c.cotizacion_id}
                  onClick={() => agregarACotizacion(c.cotizacion_id)}
                  className="w-full text-left p-3 rounded-lg border border-stone-200 hover:bg-amber-50 hover:border-amber-300 transition-colors"
                  data-testid={`cot-opt-${c.cotizacion_id}`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-stone-900 truncate">{c.cliente_nombre || "Sin cliente"}</p>
                      <p className="text-xs text-stone-500">{c.tipo_evento || c.nombre_evento} · {c.fecha_evento || "sin fecha"}</p>
                    </div>
                    <span className="text-xs font-bold text-emerald-700 shrink-0">${(c.total || 0).toLocaleString("es-MX")}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCotSelectDialog({ open: false, item: null })}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Catalogo;
